'use client';
import { useEffect, useRef } from 'react';
import { useSite, useT } from '../lib/i18n';

/**
 * 본문 스크립트 실행(2026-08-23 사용자 지시로 신설).
 *
 * 프로모션 본문은 `dangerouslySetInnerHTML` 로 들어간다. **innerHTML 로 들어간 `<script>` 는
 * 브라우저가 실행하지 않는다** — 첫 방문에는 서버가 그린 문서를 파서가 읽으므로 돌지만,
 * 사이트 안에서 링크로 들어오면(클라이언트 전환) 그대로 죽은 글자로 남는다.
 * 그래서 두 경우를 **한 갈래로 합친다** — 서버가 `type="text/promo-script"` 로 재워서 보내고
 * (파서가 실행하지도, src 를 받지도 않는다) 여기서 마운트 뒤에 깨워 순서대로 돌린다.
 * **한 번만, 겹치지 않게 돈다** — 개발 모드(StrictMode)는 이펙트를 두 번 부른다. 예전에는
 * 첫 패스가 jQuery 를 기다리는 사이 두 번째 패스가 그 다음 것(Owl)부터 곧바로 실행해
 * `jQuery is undefined` 로 터졌다(2026-08-23 사용자 로그). 그래서 두 겹으로 막는다 —
 * 컴포넌트의 실행 표시(ref)와, 시작하자마자 **동기로** 대상 전부에 도장을 찍는 것.
 *
 * **DOMContentLoaded 를 흉내 낸다** — 본문 스크립트는 대개 그 이벤트에 자기 일을 건다.
 * 우리가 늦게 돌리므로 그 이벤트는 이미 지나갔고, 그대로 두면 아무 일도 일어나지 않는다.
 * 실행하는 동안만 `document.addEventListener` 를 감싸, 이미 지난 이벤트에 다는 콜백은
 * 바로 부른다(제자리로 돌려놓는다). jQuery 의 `$(document).ready` 는 스스로 판단하므로 그대로 둔다.
 */
/** 재워 둔 표시 — 서버(`PromotionBody.sleepScripts`)가 붙인다 */
const SLEEPING = 'text/promo-script';
/** 집어 든 표시 — 다른 패스가 같은 것을 또 실행하지 않게 **동기로** 바꿔 둔다 */
const CLAIMED = 'text/promo-script-running';

async function runPromoScripts(root: HTMLElement) {
  const sleeping = Array.from(root.querySelectorAll<HTMLScriptElement>(`script[type="${SLEEPING}"]`));
  if (!sleeping.length) return;
  // 기다리기 전에 먼저 도장 — await 로 넘어간 사이 다른 패스가 뒤엣것부터 집어 가면 순서가 깨진다
  for (const s of sleeping) s.type = CLAIMED;

  const origAdd = document.addEventListener;
  document.addEventListener = function patched(this: Document, type: string, fn: unknown, opts?: unknown) {
    if (type === 'DOMContentLoaded' && document.readyState !== 'loading' && fn) {
      const call = typeof fn === 'function'
        ? () => (fn as EventListener)(new Event('DOMContentLoaded'))
        : () => (fn as EventListenerObject).handleEvent(new Event('DOMContentLoaded'));
      setTimeout(call, 0);
      return;
    }
    return (origAdd as (...a: unknown[]) => void).call(this, type, fn, opts);
  } as typeof document.addEventListener;

  try {
    for (const old of sleeping) {
      const next = document.createElement('script');
      for (const a of Array.from(old.attributes)) if (a.name !== 'type') next.setAttribute(a.name, a.value);
      if (!old.getAttribute('src')) next.text = old.textContent ?? '';
      old.replaceWith(next);
      // 바깥 파일은 받아올 때까지 기다린다 — 파서와 같은 순서(뒤 스크립트가 앞 것을 쓴다)
      if (next.src) await new Promise((done) => { next.onload = next.onerror = () => done(null); });
    }
  } finally {
    document.addEventListener = origAdd;
  }
}

/**
 * 프로모션 본문 컨테이너 — 문자열로 치환된 신청란(form[data-evt-form])의 제출을 **위임**으로 받는다.
 * React 의 onSubmit 은 버블이라 dangerouslySetInnerHTML 안에서 난 submit 도 여기 잡힌다 —
 * 본문 HTML 은 손대지 않고(하이드레이션 안전) 동작만 얹는 구조다.
 *
 * 필수값은 input 의 required 속성(브라우저 검증)이 막고, 값은 FormData 로 걷는다.
 * 결과 문구는 폼 끝의 .evt-msg 에 직접 쓴다 — 치환 HTML 안이라 React 밖 영역이다.
 */
export function PromotionForms({ promotionId, children }: {
  promotionId: number;
  children: React.ReactNode;
}) {
  const t = useT();
  const { loggedIn } = useSite();
  const box = useRef<HTMLDivElement>(null);

  const ranScripts = useRef(false);
  useEffect(() => {
    if (ranScripts.current || !box.current) return;
    ranScripts.current = true;
    void runPromoScripts(box.current);
  }, []);

  const submit = async (e: React.FormEvent) => {
    const form = e.target as HTMLFormElement;
    const formName = form.dataset.evtForm;
    if (!formName) return; // 본문 밖 폼(있다면)은 건드리지 않는다
    e.preventDefault();
    if (!loggedIn) { window.location.href = '/login'; return; }

    const values: Record<string, string | string[]> = {};
    for (const [k, v] of new FormData(form).entries()) {
      const cur = values[k];
      // 체크박스 다중 선택 — 같은 이름이 여러 번 오면 배열로
      values[k] = cur === undefined ? String(v) : Array.isArray(cur) ? [...cur, String(v)] : [cur, String(v)];
    }

    const msg = (() => {
      let el = form.querySelector<HTMLElement>('.evt-msg');
      if (!el) { el = document.createElement('p'); el.className = 'evt-msg'; form.appendChild(el); }
      return el;
    })();

    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(`/api/promotions/${promotionId}/enter`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ formName, form: values }),
      });
      if (res.status === 401) { window.location.href = '/login'; return; }
      const body = (await res.json().catch(() => ({}))) as { message?: string };
      if (!res.ok) {
        msg.className = 'evt-msg is-error';
        msg.textContent = body.message ?? t('promo.enterFail');
        if (btn) btn.disabled = false;
        return;
      }
      msg.className = 'evt-msg';
      msg.textContent = t('promo.entered');
    } catch {
      msg.className = 'evt-msg is-error';
      msg.textContent = t('promo.enterFail');
      if (btn) btn.disabled = false;
    }
  };

  return <div className="promotion-body" ref={box} onSubmit={(e) => void submit(e)}>{children}</div>;
}
